class AltoaProductDescription extends HTMLElement {
  constructor() {
    super();
    this.yamlData = null;
    this.riveInstances = [];
  }

  connectedCallback() {
    if (document.readyState === 'complete') {
      this.init();
    } else {
      window.addEventListener('load', () => this.init());
    }
  }

  init() {
    this.loadYamlData();
    this.setupIntersectionObserver();
  }

  disconnectedCallback() {
    this.riveInstances.forEach(item => {
      if (item.instance && item.instance.cleanup) { 
        item.instance.cleanup();
      }
    });
    this.riveInstances = [];
    
    if (this.observer) {
      this.observer.disconnect();
    }
    
    if (this.iconObserver) {
      this.iconObserver.disconnect();
    }
  }

  loadYamlData() {
    try {
      const template = this.querySelector('template');
      if (!template) {
        throw new Error('No template found in component');
      }
      
      const yamlText = template.textContent || template.innerHTML;
      this.yamlData = jsyaml.load(yamlText.trim());
      this.render();
    } catch (error) {
      console.error('Error loading YAML data:', error);
      this.innerHTML = '<p>Error loading product data.</p>';
    }
  }

  render() {
    if (!this.yamlData || !Array.isArray(this.yamlData)) {
      return;
    }

    const contentHtml = this.yamlData.map(section => this.renderSection(section)).join('');
    this.innerHTML = contentHtml;
    
    this.setupQAInteractions();
    this.setupRiveAnimations();
    this.observeListItems();
    this.observeIconSections();
  }

  renderSection(section) {
    if (!section || typeof section !== 'object') {
      return '';
    }

    // Handle both old format (array) and new format (object with content)
    let contentItems = [];
    if (Array.isArray(section)) {
      contentItems = section;
    } else if (section.content && Array.isArray(section.content)) {
      contentItems = section.content;
    } else {
      return '';
    }

    const sectionContent = contentItems.map(item => this.renderItem(item)).join('');
    
    // Handle background properties
    let sectionStyle = '';
    const styleProps = [];
    
    if (section.bgColor) {
      styleProps.push(`background-color: ${section.bgColor}`);
    }
    
    if (section.bgImage) {
      // Check if bgImage is a number (image parameter) or a direct URL
      let bgImageUrl = section.bgImage;
      if (!isNaN(section.bgImage)) {
        bgImageUrl = this.getAttribute(`image-${section.bgImage}`) || '';
      }
      
      if (bgImageUrl) {
        styleProps.push(`background-image: url('${bgImageUrl}')`);
        styleProps.push(`background-size: contain`);
        styleProps.push(`background-position: bottom`);
        styleProps.push(`background-repeat: no-repeat`);
      }
    }
    
    if (section.paddingBottom) {
      styleProps.push(`padding-bottom: ${section.paddingBottom}`);
    }
    
    if (styleProps.length > 0) {
      sectionStyle = ` style="${styleProps.join('; ')}"`;
    }
    
    return `<div class="yaml-section"${sectionStyle}>${sectionContent}</div>`;
  }

  renderItem(item) {
    switch (item.type) {
      case 'title':
        return `<div class="content-section"><h2>${item.text}</h2></div>`;
      
      case 'text':
        return `<div class="content-section"><p>${item.text}</p></div>`;
      
      case 'image':
        const imageSrc = this.getAttribute(`image-${item.id}`) || '';
        return `<div class="content-section"><img src="${imageSrc}" alt="${item.alt || ''}" /></div>`;
      
      case 'list':
        const pillIcon = `<svg width="24" height="29" viewBox="0 0 24 29" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="14.4715" y="1.36603" width="8.58707" height="24.997" rx="4.29354" transform="rotate(30 14.4715 1.36603)" fill="white" stroke="#833D97" stroke-width="2"/>
          <path d="M7.71777 11.5933L16.1988 16.4898" stroke="#833D97" stroke-width="2"/>
        </svg>`;
        
        const iconHtml = item.icon === 'pill' ? pillIcon : '';
        const iconClass = item.icon === 'pill' ? ' class="with-icon"' : '';
        const listItems = item.items.map(listItem => `<li${iconClass}>${iconHtml}${listItem}</li>`).join('');
        const listTitle = item.title ? `<h3>${item.title}</h3>` : '';
        return `<div class="content-section">${listTitle}<ul>${listItems}</ul></div>`;
      
      case 'qa':
        const qaItems = item.items.map((qa, index) => `
          <div class="qa-item">
            <div class="qa-question" data-qa="${index}">
              ${qa.question}
              <svg class="qa-icon" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="qa-answer" data-qa="${index}">
              ${qa.answer}
            </div>
          </div>
        `).join('');
        return `<div class="content-section qa-section">${qaItems}</div>`;
      
      case 'icons':
        const [iconWidth, iconHeight] = item.size ? item.size.split('x') : ['48', '48'];
        const iconItems = item.items ? item.items.map(iconItem => `
          <div class="icon-item">
            <div class="icon-container">
              <canvas class="rive-icons" data-rive-type="icons" data-rive-name="${iconItem.icon}" 
                      data-width="${iconWidth}" data-height="${iconHeight}"></canvas>
            </div>
            <span class="icon-text">${iconItem.text}</span>
          </div>
        `).join('') : '';
        return `<div class="content-section icons-section">${iconItems}</div>`;
      
      case 'rive':
        const [width, height] = item.size ? item.size.split('x') : ['400', '300'];
        const scrollAttr = item.scroll ? ` data-scroll="true"` : '';
        const canvasWidth = item.width || width;
        const widthAttr = item.width ? ` data-canvas-width="${item.width}"` : '';
        return `<div class="content-section rive-container">
          <canvas class="rive-animation" data-rive-type="rive" data-rive-name="${item.name}" 
                  data-width="${width}" data-height="${height}"${scrollAttr}${widthAttr}></canvas>
          ${item.title ? `<h3>${item.title}</h3>` : ''}
        </div>`;
      
      case 'html':
        return `<div class="content-section html-content">${item.content}</div>`;
      
      default:
        return item.text ? `<div class="content-section"><p>${item.text}</p></div>` : '';
    }
  }

  setupQAInteractions() {
    const questions = this.querySelectorAll('.qa-question');
    questions.forEach(question => {
      question.addEventListener('click', () => {
        const qaIndex = question.dataset.qa;
        const answer = this.querySelector(`.qa-answer[data-qa="${qaIndex}"]`);
        
        question.classList.toggle('active');
        answer.classList.toggle('active');
      });
    });
  }

  setupRiveAnimations() {
    const riveCanvases = this.querySelectorAll('[data-rive-type]');
    
    riveCanvases.forEach(canvas => {
      const riveType = canvas.dataset.riveType;
      const riveName = canvas.dataset.riveName;
      const width = canvas.dataset.width || '48';
      const height = canvas.dataset.height || '48';
      const hasScroll = canvas.dataset.scroll === 'true';
      
      // Set canvas size
      canvas.width = parseInt(width);
      canvas.height = parseInt(height);
      
      // Use custom width for styling if available, otherwise use canvas width
      const canvasWidth = canvas.dataset.canvasWidth;
      if (canvasWidth) {
        canvas.style.width = canvasWidth;
        // Don't set height when using custom width
      } else {
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
      }

      let riveSrc = '';
      if (riveType === 'icons') {
        riveSrc = this.getAttribute('rive-icon') || '';
      } else if (riveType === 'rive') {
        riveSrc = this.getAttribute('rive-image') || '';
      }

      if (riveSrc && window.rive) {
        try {
          const riveInstance = new rive.Rive({
            src: riveSrc,
            canvas: canvas,
            autoplay: riveType !== 'icons', // Autoplay for icons, not for other types
            artboard: riveName,
            stateMachines: "State Machine 1",
            onLoad: () => {
              console.log('Rive instance loaded:', riveInstance);
              // console.log('bounds:', riveInstance.artboard.bounds);

              riveInstance.resizeDrawingSurfaceToCanvas();
              
              // Setup scroll animation if enabled
              if (hasScroll) {
                this.setupScrollAnimation(canvas, riveInstance);
              }
            }
          });
          
          this.riveInstances.push({
            instance: riveInstance,
            canvas: canvas,
            type: riveType
          });
        } catch (error) {
          console.error('Error creating Rive instance:', error);
        }
      }
    });
  }

  setupScrollAnimation(canvas, riveInstance) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Start tracking scroll when element is visible
          this.trackScrollForRive(canvas, riveInstance);
        }
      });
    }, { threshold: 0.1 });
    
    observer.observe(canvas);
  }

  trackScrollForRive(canvas, riveInstance) {
    const updateScrollValue = () => {
      const rect = canvas.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Calculate when top of element is at 10% from bottom (90% from top)
      const startPosition = viewportHeight * 0.8;
      // Calculate when top of element is at 25% from top
      const endPosition = viewportHeight * 0.25;
      
      // Current position of element top
      const elementTop = rect.top;
      
      // Calculate scroll progress (0 to 100)
      let scrollProgress = 0;
      if (elementTop <= startPosition && elementTop >= endPosition) {
        const totalDistance = startPosition - endPosition;
        const currentDistance = startPosition - elementTop;
        scrollProgress = (currentDistance / totalDistance) * 100;
        scrollProgress = Math.max(0, Math.min(100, scrollProgress));
      } else if (elementTop < endPosition) {
        scrollProgress = 100;
      }
      
      // Update Rive input
      try {
        const scrollInput = riveInstance.stateMachineInputs('State Machine 1')?.find(i => i.name === 'scroll');
        if (scrollInput) {
          scrollInput.value = scrollProgress;
        }
      } catch (error) {
        console.error('Error updating scroll input:', error);
      }
    };
    
    // Update on scroll
    window.addEventListener('scroll', updateScrollValue);
    // Initial update
    updateScrollValue();
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '-25% 0px'
    });
  }

  observeListItems() {
    const listItems = this.querySelectorAll('li');
    listItems.forEach((item, index) => {
      // Add a small delay to prevent immediate visibility on page load
      setTimeout(() => {
        this.observer.observe(item);
      }, 100 + (index * 50));
    });
  }

  observeIconSections() {
    this.iconObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.startIconAnimations(entry.target);
          this.iconObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '-30% 0px'
    });

    const iconSections = this.querySelectorAll('.icons-section');
    iconSections.forEach(section => {
      this.iconObserver.observe(section);
    });
  }

  startIconAnimations(iconSection) {
    const iconItems = iconSection.querySelectorAll('.icon-item');
    
    iconItems.forEach((iconItem, index) => {
      setTimeout(() => {
        const canvas = iconItem.querySelector('canvas[data-rive-type="icons"]');
        const iconText = iconItem.querySelector('.icon-text');
        
        // Start Rive animation
        const riveItem = this.riveInstances.find(item => 
          item.canvas === canvas && item.type === 'icons'
        );
        
        if (riveItem && riveItem.instance) {
          try {
            // Trigger animation by playing the state machine
            riveItem.instance.play();
            
            // If there's a trigger input, fire it
            const triggers = riveItem.instance.stateMachineInputs('State Machine 1');
            if (triggers) {
              const startTrigger = triggers.find(input => 
                input.name === 'start' || input.name === 'trigger' || input.name === 'play'
              );
              if (startTrigger && startTrigger.fire) {
                startTrigger.fire();
              }
            }
          } catch (error) {
            console.error('Error starting icon animation:', error);
          }
        }
        
        // Reveal label text with same timing
        if (iconText) {
          iconText.classList.add('visible');
        }
      }, index * 300); // 300ms delay between each icon
    });
  }
}

if (typeof customElements !== 'undefined') {
  customElements.define('altoa-product-description', AltoaProductDescription);
} else {
  console.error('Custom elements not supported');
}